import { useState } from 'react';
import type { JSX } from 'react';
import { AlertTriangle, CheckCircle2, Download, Globe, Loader2, RotateCcw, Settings, ShieldAlert, Smartphone, X } from 'lucide-react';
import { istNativeApp } from '../../services/nativePlatform';
import { useAppUpdate, type InstallZustand } from '../../services/appUpdate';
import { apkUpdateVerfuegbar } from '../../services/apkUpdate';

/**
 * Update-Leiste der Android-App (docs/android-app-plan.md Abschnitt 4, P6;
 * Update in der App: Vertrag E21).
 *
 *   'hinweis' → wegklickbare Leiste „Neue App-Version X verfügbar —
 *               Aktualisieren", im Fluss oben in <main> (wie der
 *               Bauphase-Balken). Weggeklickt gilt je versionCode: die
 *               naechste Version meldet sich wieder.
 *   'pflicht' → blockierende Vollflaeche mit Knopf. Sie ist ein Dialog
 *               (`role="alertdialog"`, `fixed inset-0`) — die Android-
 *               Zurueck-Taste laesst sie deshalb stehen (appUmfang,
 *               DIALOG_SELEKTOR).
 *   Browser   → null.
 *
 * „Aktualisieren" laedt und prueft IN der App (Phase 1, abbrechbar); danach
 * „Update bereit" mit „Jetzt installieren" (Phase 2: Eingaben sichern, dann
 * installieren — die App wird dabei beendet; Review R4). Leiste und
 * Vollflaeche zeigen dieselben Stufen (Laden mit Prozent, Pruefen, Bereit,
 * Sichern, Erlaubnis, Bestaetigen, Installieren, Fehler mit Rueckfall „Im
 * Browser laden"). Laeuft
 * ein Update, erscheint die Leiste auch dann, wenn sie weggeklickt war — der
 * Anstoss kann von der Download-Seite kommen.
 *
 * Der Merker liegt im localStorage, gekapselt in try/catch: ohne Speicher
 * (privater Modus, gesperrt) gilt das Wegklicken bis zum Neustart der App.
 */

const WEGGEKLICKT_SCHLUESSEL = 'appUpdate.weggeklicktFuerVersionCode';

const leseWeggeklickt = (): number | null => {
    try {
        const wert = Number(window.localStorage.getItem(WEGGEKLICKT_SCHLUESSEL));
        return Number.isInteger(wert) && wert > 0 ? wert : null;
    } catch {
        return null;
    }
};

const merkeWeggeklickt = (versionCode: number): void => {
    try {
        window.localStorage.setItem(WEGGEKLICKT_SCHLUESSEL, String(versionCode));
    } catch {
        // ohne Speicher gilt das Wegklicken nur fuer diese Sitzung
    }
};

type Darstellung = 'leiste' | 'karte';

interface AnzeigeProps {
    installation: InstallZustand;
    darstellung: Darstellung;
    /** Phase 1 erneut (Einstellung öffnen, Erneut versuchen). */
    onStart: () => void;
    /** Phase 2: sichern + installieren. */
    onInstallieren: () => void;
    onAbbrechen: () => void;
    onImBrowser: () => void;
    onErneut: () => void;
}

const NEU_OEFFNEN = 'Die App schließt sich dabei und muss danach neu geöffnet werden.';
/** Text der Stufe `bereit` (Entscheid R4, P2-3). */
const BEREIT_TEXT = 'Update bereit — die App wird beim Installieren geschlossen. Eingaben werden vorher gesichert.';

/** Fortschrittsbalken; ohne bekannte Groesse nur der Text. */
const Fortschritt = ({ prozent, darstellung }: { prozent: number | null; darstellung: Darstellung }): JSX.Element | null => {
    if (prozent === null) return null;
    const leiste = darstellung === 'leiste';
    return (
        <div
            role="progressbar"
            aria-label="Download der neuen Version"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={prozent}
            className={leiste ? 'mt-1 h-1 w-full rounded-full bg-emerald-800/50 overflow-hidden' : 'mt-3 h-2 w-full rounded-full bg-slate-100 overflow-hidden'}
        >
            <div
                className={leiste ? 'h-full bg-white transition-[width] duration-300' : 'h-full bg-emerald-600 transition-[width] duration-300'}
                style={{ width: `${prozent}%` }}
            />
        </div>
    );
};

/**
 * Die Stufen des In-App-Updates (alles ausser 'ruhe') — in der Leiste
 * kompakt auf gruenem bzw. rotem Grund, auf der Vollflaeche als Block in der
 * weissen Karte.
 */
const InstallAnzeige = ({ installation, darstellung, onStart, onInstallieren, onAbbrechen, onImBrowser, onErneut }: AnzeigeProps): JSX.Element | null => {
    const leiste = darstellung === 'leiste';
    const haupt = leiste ? 'font-semibold' : 'text-sm font-semibold text-slate-800';
    const neben = leiste ? 'text-xs text-emerald-50' : 'mt-1 text-xs text-slate-500 leading-relaxed';
    const knopfHaupt = leiste
        ? `shrink-0 inline-flex items-center gap-1.5 bg-white font-semibold px-3 py-1 rounded-lg text-xs ${installation.phase === 'fehler' ? 'text-rose-700 hover:bg-rose-50' : 'text-emerald-700 hover:bg-emerald-50'}`
        : 'mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-sm font-semibold px-4 py-3 transition-colors';
    const knopfNeben = leiste
        ? 'shrink-0 inline-flex items-center gap-1.5 border border-white/70 text-white hover:bg-white/10 font-semibold px-3 py-1 rounded-lg text-xs'
        : 'mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold px-4 py-2.5 transition-colors';
    const symbol = leiste ? 'w-4 h-4 shrink-0' : 'w-5 h-5 shrink-0 text-emerald-600';

    const zeile = (icon: JSX.Element, titel: string, text?: string): JSX.Element => (
        <div className={leiste ? 'flex items-start gap-3 min-w-0 flex-1' : 'flex items-start gap-3 text-left'}>
            {icon}
            <div className="min-w-0">
                <p className={haupt}>{titel}</p>
                {text && <p className={neben}>{text}</p>}
            </div>
        </div>
    );
    const drehend = <Loader2 className={`${symbol} animate-spin`} aria-hidden="true" />;

    switch (installation.phase) {
        case 'laden':
            return (
                <div className={leiste ? 'min-w-0 flex-1' : ''}>
                    {zeile(
                        drehend,
                        installation.prozent === null ? 'Wird geladen …' : `Wird geladen … ${installation.prozent} %`,
                    )}
                    <Fortschritt prozent={installation.prozent} darstellung={darstellung} />
                    <button type="button" onClick={onAbbrechen} className={leiste ? `${knopfNeben} mt-2` : knopfNeben}>
                        <X className="w-3.5 h-3.5" />
                        Abbrechen
                    </button>
                </div>
            );
        case 'pruefen':
            return zeile(drehend, 'Wird geprüft …');
        case 'bereit':
            return (
                <>
                    {zeile(<Download className={symbol} aria-hidden="true" />, BEREIT_TEXT)}
                    <button type="button" onClick={onInstallieren} className={knopfHaupt}>
                        <Smartphone className={leiste ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
                        Jetzt installieren
                    </button>
                </>
            );
        case 'sichern':
            return zeile(drehend, 'Eingaben werden gesichert …', NEU_OEFFNEN);
        case 'installieren':
            return zeile(drehend, 'Wird installiert …', NEU_OEFFNEN);
        case 'bestaetigen':
            return (
                <>
                    {zeile(
                        <Smartphone className={symbol} aria-hidden="true" />,
                        'Bitte Installation bestätigen',
                        `Im Android-Dialog „Aktualisieren" bzw. „Installieren" tippen. ${NEU_OEFFNEN}`,
                    )}
                    <button type="button" onClick={onErneut} className={knopfNeben}>
                        <RotateCcw className="w-3.5 h-3.5" />
                        Dialog erneut öffnen
                    </button>
                </>
            );
        case 'erlaubnis_noetig':
            return (
                <>
                    {zeile(
                        <ShieldAlert className={leiste ? symbol : 'w-5 h-5 shrink-0 text-amber-600'} aria-hidden="true" />,
                        'Einmalig erlauben: Apps aus dieser Quelle zulassen',
                        'In der Einstellung den Schalter für diese App einschalten und danach zurück in die App wechseln — das Update läuft dann von selbst weiter.',
                    )}
                    <button type="button" onClick={onStart} className={knopfHaupt}>
                        <Settings className={leiste ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
                        Einstellung öffnen
                    </button>
                </>
            );
        case 'fehler':
            if (installation.nurInstallation) {
                // Sichern nicht bestaetigt (Codex R4): nur erneut sichern + installieren —
                // kein Browser-Rueckfall, kein „trotzdem installieren".
                return (
                    <>
                        {zeile(
                            <AlertTriangle className={leiste ? symbol : 'w-5 h-5 shrink-0 text-rose-600'} aria-hidden="true" />,
                            'Update nicht installiert',
                            installation.meldung ?? undefined,
                        )}
                        <button type="button" onClick={onInstallieren} className={knopfHaupt}>
                            <RotateCcw className={leiste ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
                            Erneut versuchen
                        </button>
                    </>
                );
            }
            return (
                <>
                    {zeile(
                        <AlertTriangle className={leiste ? symbol : 'w-5 h-5 shrink-0 text-rose-600'} aria-hidden="true" />,
                        'Update fehlgeschlagen',
                        installation.meldung ?? undefined,
                    )}
                    <button type="button" onClick={onImBrowser} className={knopfHaupt}>
                        <Globe className={leiste ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
                        Im Browser laden
                    </button>
                    <button type="button" onClick={onStart} className={knopfNeben}>
                        <RotateCcw className="w-3.5 h-3.5" />
                        Erneut versuchen
                    </button>
                </>
            );
        case 'fertig':
            return zeile(
                <CheckCircle2 className={symbol} aria-hidden="true" />,
                'Update installiert',
                'Bitte die App neu öffnen.',
            );
        default:
            return null;
    }
};

export function AppUpdateBanner(): JSX.Element | null {
    const [nativ] = useState<boolean>(() => istNativeApp());
    const [inApp] = useState<boolean>(() => apkUpdateVerfuegbar());
    const {
        status, release, oeffneDownload, installation, jetztInstallieren, abbrechen, imBrowserLaden, erneutVersuchen, zuruecksetzen,
    } = useAppUpdate();
    const [weggeklickt, setWeggeklickt] = useState<number | null>(() => (nativ ? leseWeggeklickt() : null));

    if (!nativ || !release) return null;
    const inArbeit = installation.phase !== 'ruhe';
    if (status === 'aktuell' && !inArbeit) return null;

    if (status === 'pflicht') {
        return (
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="app-update-pflicht-titel"
                className="fixed inset-0 z-[10000] bg-slate-900/95 flex items-center justify-center px-6"
                style={{
                    paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
                    paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
                }}
            >
                <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 text-center">
                    <div className="mx-auto mb-4 w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                        <Smartphone className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h2 id="app-update-pflicht-titel" className="text-lg font-bold text-slate-800">App aktualisieren</h2>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                        Diese App-Version wird nicht mehr unterstützt. Bitte installieren Sie die neue
                        Version {release.versionName}.
                    </p>
                    {release.notes && (
                        <p className="mt-2 text-xs text-slate-500 leading-relaxed whitespace-pre-line">{release.notes}</p>
                    )}
                    {inArbeit ? (
                        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/60 p-4" role="status" aria-live="polite">
                            <InstallAnzeige
                                installation={installation}
                                darstellung="karte"
                                onStart={oeffneDownload}
                                onInstallieren={jetztInstallieren}
                                onAbbrechen={abbrechen}
                                onImBrowser={imBrowserLaden}
                                onErneut={erneutVersuchen}
                            />
                        </div>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={oeffneDownload}
                                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-sm font-semibold px-4 py-3 transition-colors"
                            >
                                <Download className="w-4 h-4" />
                                Aktualisieren
                            </button>
                            <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                                {inApp
                                    ? 'Die neue Version wird in der App geladen und geprüft; installiert wird erst auf „Jetzt installieren". Anmeldung und Daten bleiben erhalten.'
                                    : 'Der Download öffnet sich im Browser. Android installiert die neue Version über die alte — Anmeldung und Daten bleiben erhalten.'}
                                {' '}Bis dahin funktioniert die Web-App im Browser wie gewohnt.
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }

    if (!inArbeit && weggeklickt === release.versionCode) return null;

    const schliessen = (): void => {
        merkeWeggeklickt(release.versionCode);
        setWeggeklickt(release.versionCode);
        zuruecksetzen();
    };
    // Waehrend Laden/Pruefen/Sichern/Installieren/Bestaetigen kein Wegklicken: der Lauf ginge unsichtbar weiter.
    const schliessbar = installation.phase === 'ruhe' || installation.phase === 'bereit' || installation.phase === 'fehler'
        || installation.phase === 'erlaubnis_noetig' || installation.phase === 'fertig';
    const grund = installation.phase === 'fehler' ? 'bg-rose-700' : 'bg-emerald-600';

    return (
        <div className={`shrink-0 ${grund} text-white`} role="status" aria-live="polite" data-glass-view="app-leiste">
            <div className="px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
                {inArbeit ? (
                    <InstallAnzeige
                        installation={installation}
                        darstellung="leiste"
                        onStart={oeffneDownload}
                        onInstallieren={jetztInstallieren}
                        onAbbrechen={abbrechen}
                        onImBrowser={imBrowserLaden}
                        onErneut={erneutVersuchen}
                    />
                ) : (
                    <>
                        <Download className="w-4 h-4 shrink-0" />
                        <span className="flex-1 min-w-0">Neue App-Version {release.versionName} verfügbar —</span>
                        <button
                            type="button"
                            onClick={oeffneDownload}
                            className="shrink-0 bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-3 py-1 rounded-lg text-xs"
                        >
                            Aktualisieren
                        </button>
                    </>
                )}
                {schliessbar && (
                    <button
                        type="button"
                        onClick={schliessen}
                        aria-label="Hinweis ausblenden"
                        className={`shrink-0 p-1 rounded-lg transition-colors ${installation.phase === 'fehler' ? 'hover:bg-rose-800' : 'hover:bg-emerald-700'}`}
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}
